<script setup lang="ts">
import { computed } from 'vue';

interface HelperPicture {
  picture: string;
  size: string;
  margin: string;
  index: string;
}

interface HelperHighlighted {
  profilePicture?: string;
  firstname?: string;
  lastname?: string;
}

const props = defineProps<{
  headerType?: 'image' | 'community';
  headerBackgroundUrl: string;
  helpersProfilePicture: HelperPicture[];
  helperHighlighted?: HelperHighlighted;
  hasHelpersProfilePictures?: boolean;
  widget: boolean;
  containerWidth: number;
}>();

const isCommunityHeader = computed(() => props.headerType === 'community');

const highlightedName = computed(() => {
  const hasHelpers = props.hasHelpersProfilePictures;
  const firstname = hasHelpers
    ? props.helperHighlighted?.firstname || ''
    : 'Marine';
  const lastnameInitial = hasHelpers
    ? props.helperHighlighted?.lastname?.charAt(0) || ''
    : 'B';

  return `${firstname} ${lastnameInitial}.`;
});

const highlightedPicture = computed(() => {
  if (props.hasHelpersProfilePictures) {
    return props.helperHighlighted?.profilePicture || '';
  }

  return '/img/team/marine.png';
});
</script>

<template>
  <div
    :class="[
      'min-h-[200px]',
      {
        'bg-cover bg-center': headerType === 'image',
        'flex flex-col items-center px-6 pt-6': isCommunityHeader,
        'md:rounded-t-[15px]': widget && containerWidth >= 600
      }
    ]"
    :style="{
      backgroundImage: headerType === 'image' ? headerBackgroundUrl : undefined
    }"
  >
    <template v-if="isCommunityHeader">
      <div class="flex flex-shrink-0 items-center -space-x-2">
        <div
          v-for="(helper, helperIndex) in helpersProfilePicture"
          :key="helperIndex"
          :class="[helper.index, helper.margin]"
        >
          <div
            :class="[
              'overflow-hidden rounded-full',
              helper.size,
              {
                'border-2 border-border-on-emphasis': hasHelpersProfilePictures
              }
            ]"
          >
            <img class="h-full w-full object-cover" :src="helper.picture" />
          </div>
        </div>
      </div>

      <div
        class="z-[8] -mt-3.5 flex max-w-[85%] items-start rounded-2xl border border-border-subtle bg-background-default p-3.5 shadow-[0px_2.63084px_3.94626px_-1.31542px_rgba(28,31,39,0.03),0px_7.89252px_10.52336px_-2.63084px_rgba(28,31,39,0.08)]"
      >
        <div class="relative">
          <div
            :class="[
              'h-[38px] w-[38px] overflow-hidden rounded-full',
              {
                'border-2 border-border-on-emphasis': hasHelpersProfilePictures
              }
            ]"
          >
            <img class="h-full w-full object-cover" :src="highlightedPicture" />
          </div>

          <div
            class="absolute bottom-0 right-0 h-[15px] w-[15px] overflow-hidden"
          >
            <img
              class="h-full w-full object-cover"
              src="/img/logos/instagram.svg"
            />
          </div>
        </div>

        <div class="ml-3.5 text-mgm-txt-xs">
          <div class="flex items-center justify-between">
            <span class="block font-semibold">
              {{ highlightedName }}
            </span>

            <span class="block font-normal text-foreground-subtle"> 9:41 </span>
          </div>

          <span class="block font-normal text-foreground-default">
            {{ $t('helpeeSignUp.creation.header.community.msg') }}
          </span>
        </div>
      </div>
    </template>
  </div>
</template>
